/**
 * Error Handler Utility
 * Phase 3: 아키텍처 표준화 - 에러 처리 표준화
 *
 * @description
 * - 일관된 에러 처리 패턴
 * - 에러 코드 기반 분류
 * - 사용자 대면 에러 구분
 */

import { logger } from './logger.ts';

// ============================================
// Error Codes
// ============================================

/**
 * 표준 에러 코드
 */
export const ErrorCodes = {
  /** 인증 관련 에러 */
  AUTH_ERROR: 'AUTH_ERROR',
  /** 네트워크 관련 에러 */
  NETWORK_ERROR: 'NETWORK_ERROR',
  /** 유효성 검사 에러 */
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  /** Firebase 관련 에러 */
  FIREBASE_ERROR: 'FIREBASE_ERROR',
  /** 데이터 처리 에러 */
  DATA_ERROR: 'DATA_ERROR',
  /** 알 수 없는 에러 */
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes];

// ============================================
// AppError Class
// ============================================

/**
 * 애플리케이션 커스텀 에러 클래스
 *
 * @example
 * throw new AppError(
 *   '데이터를 불러올 수 없습니다',
 *   ErrorCodes.FIREBASE_ERROR,
 *   { documentId: 'doc123' },
 *   true // 사용자에게 표시
 * );
 */
export class AppError extends Error {
  public readonly name = 'AppError';

  constructor(
    message: string,
    public readonly code: string,
    public readonly context?: object,
    public readonly isUserFacing: boolean = false
  ) {
    super(message);

    // ES5 환경에서 Error 상속 시 prototype chain 수정 필요
    Object.setPrototypeOf(this, AppError.prototype);

    // 스택 트레이스 캡처
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }
  }

  /**
   * 에러를 JSON 형태로 직렬화
   */
  toJSON(): object {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      context: this.context,
      isUserFacing: this.isUserFacing,
      stack: this.stack,
    };
  }
}

// ============================================
// Error Handler
// ============================================

/**
 * 통합 에러 처리 함수
 *
 * @param error 처리할 에러 (unknown 타입 지원)
 * @param fallbackMessage 폴백 메시지 (에러 타입 미확인 시 사용)
 *
 * @example
 * try {
 *   await fetchData();
 * } catch (error) {
 *   handleError(error, '데이터를 불러오는데 실패했습니다');
 * }
 */
export function handleError(error: unknown, fallbackMessage: string): void {
  if (error instanceof AppError) {
    // AppError: 원본 메시지와 context 사용
    logger.error(error.message, {
      code: error.code,
      context: error.context,
      isUserFacing: error.isUserFacing,
    } as unknown as Error);

    // 사용자 대면 에러 처리 (추후 toast 등으로 확장)
    if (error.isUserFacing) {
      // TODO: showToast(error.message, 'error');
    }
  } else if (error instanceof Error) {
    // 일반 Error: fallback 메시지 사용
    logger.error(fallbackMessage, { error } as unknown as Error);
  } else {
    // unknown 타입: fallback 메시지만 사용
    logger.error(fallbackMessage, {
      originalError: error,
    } as unknown as Error);
  }
}

// ============================================
// Helper Functions
// ============================================

/**
 * 에러인지 확인하는 타입 가드
 */
export function isError(value: unknown): value is Error {
  return value instanceof Error;
}

/**
 * AppError인지 확인하는 타입 가드
 */
export function isAppError(value: unknown): value is AppError {
  return value instanceof AppError;
}

/**
 * 에러 메시지 추출
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return '알 수 없는 오류가 발생했습니다';
}

/**
 * 에러 코드 추출
 */
export function getErrorCode(error: unknown): string {
  if (error instanceof AppError) {
    return error.code;
  }
  return ErrorCodes.UNKNOWN_ERROR;
}

export default {
  AppError,
  ErrorCodes,
  handleError,
  isError,
  isAppError,
  getErrorMessage,
  getErrorCode,
};
